import React from 'react';
import PropTypes from 'prop-types';
import gql from 'graphql-tag';
import { useMutation } from '@apollo/react-hooks';
import { useIntl, defineMessages } from 'react-intl';
import { Button } from 'primereact/button';
import { growlShape } from '../../components/prop-types';
import { withGrowl } from '../../components/GrowlNotifications';
import { growlActionSuccess, growlErrors } from '../../utils/NotificationUtil';

const i18nMessages = defineMessages({
  updateTranslations: {
    id: 'menu.item-bank-management.update-translations',
    defaultMessage: 'Update Translations',
  },
  previewContent: {
    id: 'menu.item-bank-management.preview-content',
    defaultMessage: 'Preview Content',
  },
});

const UPDATE_ITEM_BANK_TRANSLATIONS = gql`
  mutation updateItemBankTranslationsMutation($input: ItemBankTranslationsInput!) {
    updateItemBankTranslations(input: $input) {
      project
      component
      language
    }
  }
`;

const PREVIEW__TRANSLATIONS_CONTENT = gql`
  mutation previewTranslationsContentMutation($input: ItemBankTranslationsInput!) {
    previewTranslationsContent(input: $input) {
      content
    }
  }
`;

const downloadContent = (content, translationProjectUrlSlug, translationComponentUrlSlug, language) => {
  const contentJsonObject = JSON.parse(content);
  const contentJsonString = JSON.stringify(contentJsonObject, null, 2);
  const bytes = new TextEncoder().encode(contentJsonString);

  const blob = new Blob([bytes], {
    type: 'application/json;charset=utf-8',
  });

  const name = `${translationProjectUrlSlug}-${translationComponentUrlSlug}-${language}.json`;
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;

  link.setAttribute('download', name);
  link.setAttribute('target', '_blank');
  document.body.appendChild(link);
  link.click();

  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

const TranslationComponent = ({
  translationProjectUrlSlug,
  translationComponent,
  accountId,
  selectedLanguage,
  growl,
}) => {
  const { urlSlug, displayName } = translationComponent;

  const [updateTranslationsMutation] = useMutation(UPDATE_ITEM_BANK_TRANSLATIONS);
  const [previewTranslationsContentMutation] = useMutation(PREVIEW__TRANSLATIONS_CONTENT);

  const { formatMessage } = useIntl();

  const updateTranlations = async () => {
    try {
      await updateTranslationsMutation({
        variables: {
          input: {
            accountId,
            project: translationProjectUrlSlug,
            component: urlSlug,
            language: selectedLanguage,
          },
        },
      });
      growlActionSuccess(growl);
    } catch (mutationError) {
      growlErrors(growl, mutationError);
    }
  };

  const previewTranslationsContent = async () => {
    try {
      const result = await previewTranslationsContentMutation({
        variables: {
          input: {
            accountId,
            project: translationProjectUrlSlug,
            component: urlSlug,
            language: selectedLanguage,
          },
        },
      });

      downloadContent(result.data.previewTranslationsContent.content,
        translationProjectUrlSlug,
        urlSlug,
        selectedLanguage);

      growlActionSuccess(growl);
    } catch (mutationError) {
      growlErrors(growl, mutationError);
    }
  };

  return (
    <div className="component">
      <h4 className="display-name">{displayName}</h4>
      <div className="buttons">
        <Button
          type="button"
          icon="pi pi-plus"
          label={formatMessage(i18nMessages.updateTranslations)}
          onClick={() => updateTranlations(translationProjectUrlSlug, urlSlug)}
        />
        <Button
          type="button"
          label={formatMessage(i18nMessages.previewContent)}
          className="p-button-success"
          onClick={previewTranslationsContent}
        />
      </div>
    </div>
  );
};

TranslationComponent.propTypes = {
  translationProjectUrlSlug: PropTypes.string.isRequired,
  translationComponent: PropTypes.objectOf(PropTypes.any).isRequired,
  selectedLanguage: PropTypes.string.isRequired,
  accountId: PropTypes.string.isRequired,
  growl: growlShape.isRequired,
};

export default withGrowl(TranslationComponent);
